import { useCallback, useEffect, useState } from 'react'
import { Clock, LayoutGrid, Loader2, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  assignChairSession,
  completeChairSession,
  fetchChairBoard,
  finishChairTurnover,
  startChairSession,
} from '@/lib/api/chairBoard'
import { fetchWalkinQueue } from '@/lib/api/walkinQueue'
import {
  chairStatusLabel,
  chairStatusStyles,
  formatRemainingLabel,
  primaryChairAction,
} from '@/lib/calendar/chairBoard'
import { queueGuestLabel } from '@/lib/calendar/walkinQueue'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

/**
 * @param {{
 *   queueEntries?: import('@/types/api').WalkinQueueEntry[]
 *   onUpdated?: () => void
 *   compact?: boolean
 * }} props
 */
export function ChairBoardGrid({ queueEntries: externalQueueEntries, onUpdated, compact = false }) {
  const [board, setBoard] = useState(null)
  const [internalQueueEntries, setInternalQueueEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [actingId, setActingId] = useState(null)
  const [error, setError] = useState('')

  const queueEntries = externalQueueEntries ?? internalQueueEntries

  const loadBoard = useCallback(async (options = {}) => {
    const silent = options.silent === true
    if (!silent) {
      setLoading(true)
    }
    setError('')

    try {
      const requests = [fetchChairBoard()]
      if (externalQueueEntries === undefined) {
        requests.push(fetchWalkinQueue())
      }

      const results = await Promise.all(requests)
      setBoard(results[0])

      if (externalQueueEntries === undefined && results[1]) {
        setInternalQueueEntries(results[1])
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load chair board.'))
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }, [externalQueueEntries])

  useEffect(() => {
    loadBoard()
  }, [loadBoard])

  /**
   * @param {() => Promise<import('@/types/api').ChairBoardPayload>} action
   */
  async function runAction(action) {
    setActingId('busy')
    setError('')

    try {
      const nextBoard = await action()
      setBoard(nextBoard)
      onUpdated?.()
      await loadBoard({ silent: true })
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to update chair session.'))
    } finally {
      setActingId(null)
    }
  }

  /**
   * @param {number} chairId
   * @param {string} queueEntryId
   */
  async function assignFromQueue(chairId, queueEntryId) {
    const entry = queueEntries.find((item) => String(item.id) === queueEntryId)
    if (!entry) {
      return
    }

    await runAction(() =>
      assignChairSession({
        chair_id: chairId,
        walkin_queue_id: entry.id,
        customer_id: entry.customer_id ?? null,
        stylist_id: entry.stylist_id ?? null,
      }),
    )
  }

  /**
   * @param {import('@/types/api').ChairBoardRoom} room
   */
  async function handlePrimaryAction(room) {
    const action = primaryChairAction(room)
    if (!action || !room.session_id) {
      return
    }

    setActingId(room.session_id)

    try {
      let nextBoard
      if (action.type === 'start') {
        nextBoard = await startChairSession(room.session_id)
      } else if (action.type === 'complete') {
        nextBoard = await completeChairSession(room.session_id, { start_turnover: true })
      } else {
        nextBoard = await finishChairTurnover(room.session_id)
      }

      setBoard(nextBoard)
      onUpdated?.()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to update chair session.'))
    } finally {
      setActingId(null)
    }
  }

  if (loading && !board) {
    return (
      <section className="card-surface flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading chair board…
      </section>
    )
  }

  const stats = board?.stats ?? { occupied: 0, available: 0, turnover: 0, occupancy_pct: 0 }
  const rooms = board?.rooms ?? []

  return (
    <section className={cn('card-surface', compact ? 'p-3' : 'p-4 lg:p-5')}>
      <div className={cn('flex flex-wrap items-center justify-between gap-2', compact ? 'mb-2' : 'mb-4')}>
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <LayoutGrid className="h-4 w-4 text-primary" />
            Chair board
          </p>
          <p className="text-[11px] text-muted-foreground">
            {stats.occupancy_pct}% occupied · {stats.occupied} busy · {stats.available} ready
            {!compact && stats.turnover > 0 ? ` · ${stats.turnover} turnover` : ''}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading || actingId !== null}
          onClick={() => loadBoard()}
        >
          <RefreshCw className={cn('mr-1 h-3.5 w-3.5', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {error ? (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      <div
        className={cn(
          'grid gap-2',
          compact ? 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
        )}
      >
        {rooms.map((room) => {
          const styles = chairStatusStyles(room.status)
          const action = primaryChairAction(room)

          return (
            <article
              key={room.id}
              className={cn(
                'rounded-xl border border-border/50 bg-gradient-to-b shadow-sm',
                compact ? 'p-2.5' : 'rounded-2xl p-3.5 lg:p-4',
                styles.card,
              )}
            >
              <div className="flex items-start justify-between gap-1.5">
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    {room.full_name ?? room.name}
                  </p>
                  {room.service && !compact ? (
                    <p className="mt-0.5 truncate text-xs font-semibold text-foreground">{room.service}</p>
                  ) : null}
                </div>
                <span className={cn('shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase', styles.badge)}>
                  {chairStatusLabel(room.status)}
                </span>
              </div>

              {room.guest ? (
                <div className={cn(compact ? 'mt-1.5' : 'mt-3')}>
                  <p className={cn('font-bold text-foreground', compact ? 'truncate text-xs' : 'text-sm')}>
                    {room.guest}
                  </p>
                  {room.therapist && !compact ? (
                    <p className="text-[11px] text-muted-foreground">with {room.therapist}</p>
                  ) : null}
                  {compact && room.service ? (
                    <p className="truncate text-[10px] text-muted-foreground">{room.service}</p>
                  ) : null}
                </div>
              ) : (
                <p className={cn('text-muted-foreground', compact ? 'mt-1.5 text-[10px]' : 'mt-3 text-xs')}>
                  {room.readiness ?? 'Available'}
                </p>
              )}

              {!compact && (room.status === 'occupied' || room.status === 'turnover') ? (
                <div className="mt-3 rounded-xl bg-muted/30 px-3 py-2">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="font-semibold text-primary">
                      {room.occupancy_pct ?? 0}% session
                    </span>
                    <span className="flex items-center gap-1 font-semibold tabular-nums">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      {formatRemainingLabel(room.remaining_min)}
                    </span>
                  </div>
                  {(room.occupancy_pct ?? 0) > 0 ? (
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/90">
                      <div
                        className={cn('h-full rounded-full bg-gradient-to-r', styles.bar)}
                        style={{ width: `${room.occupancy_pct}%` }}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {compact && (room.status === 'occupied' || room.status === 'turnover') ? (
                <p className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold tabular-nums text-primary">
                  <Clock className="h-3 w-3" />
                  {formatRemainingLabel(room.remaining_min)}
                </p>
              ) : null}

              {room.status === 'available' && queueEntries.length > 0 ? (
                <label className={cn(compact ? 'mt-2' : 'mt-3', 'block')}>
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {compact ? 'Walk-in' : 'Assign walk-in'}
                  </span>
                  <select
                    disabled={actingId !== null}
                    defaultValue=""
                    onChange={(event) => {
                      const value = event.target.value
                      if (!value) return
                      assignFromQueue(room.id, value)
                      event.target.value = ''
                    }}
                    className={cn(
                      'w-full rounded-lg border border-input bg-background px-2 text-xs',
                      compact ? 'py-1' : 'py-1.5',
                    )}
                  >
                    <option value="">Select guest…</option>
                    {queueEntries.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {queueGuestLabel(entry)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {action ? (
                <Button
                  type="button"
                  size="sm"
                  className={cn('w-full', compact ? 'mt-2 h-7 text-xs' : 'mt-3')}
                  disabled={actingId === room.session_id}
                  onClick={() => handlePrimaryAction(room)}
                >
                  {actingId === room.session_id ? 'Updating…' : action.label}
                </Button>
              ) : null}

              {!compact && room.next_booking ? (
                <p className="mt-2 text-[10px] text-muted-foreground">
                  Next: {room.next_booking} {room.next_booking_time ? `· ${room.next_booking_time}` : ''}
                </p>
              ) : null}
            </article>
          )
        })}
      </div>
    </section>
  )
}
